import React, { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { Box, IconButton, HStack, Text, Heading, ListItem, UnorderedList, OrderedList, Image, Table, Thead, Tbody, Tr, Th, Td, useToast, Spinner } from '@chakra-ui/react';
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { FaDownload, FaCopy, FaCheck } from 'react-icons/fa';
import remarkGfm from 'remark-gfm';


const programmingLanguages = {
    javascript: ".js",
    python: ".py",
    java: ".java",
    c: ".c",
    cpp: ".cpp",
    "c++": ".cpp",
    "c#": ".cs",
    ruby: ".rb",
    php: ".php",
    swift: ".swift",
    "objective-c": ".m",
    kotlin: ".kt",
    typescript: ".ts",
    go: ".go",
    perl: ".pl",
    rust: ".rs",
    scala: ".scala",
    haskell: ".hs",
    lua: ".lua",
    shell: ".sh",
    sql: ".sql",
    html: ".html",
    css: ".css",
    // Add any other languages and extensions you need
  };

const MarkdownRenderer = ({ content }) => {
  const [copiedCode, setCopiedCode] = useState(null);
  const [downloadingLink, setDownloadingLink] = useState(null);
  const toast = useToast();

  const cleanUrlCorrectionMarkers = (text) => {
    if (!text || typeof text !== 'string') return text;
    return text.replace(/.*?\[URL_CORRECTION_START\](.*?)\[URL_CORRECTION_END\]/gs, '$1');
  };

  const onCopy = async (text) => {
    await navigator.clipboard.writeText(text);
    setCopiedCode(text);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const downloadAsFile = (content, language) => {
    const extension = programmingLanguages[language.toLowerCase()] || '.txt';
    const element = document.createElement("a");
    const file = new Blob([content], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = `code${extension}`;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  return (
    <Box>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
            code({ node, inline, className, children, ...props }) {
                const childArray = React.Children.toArray(children);
                const firstChild = childArray[0];
    
                let firstChildAsString = '';
    
                if (typeof firstChild === 'string') {
                  firstChildAsString = firstChild;
                } else if (React.isValidElement(firstChild) && typeof firstChild.props.children === 'string') {
                  firstChildAsString = firstChild.props.children;
                }
    
                if (firstChildAsString === '▍') {
                  return <span className="mt-1 animate-pulse cursor-default">▍</span>;
                }
    
                if (typeof firstChildAsString === 'string') {
                  childArray[0] = firstChildAsString.replace(/`▍`/g, '▍');
                }
    
                const match = /language-(\w+)/.exec(className || '');
                
                if (
                  typeof firstChildAsString === "string" &&
                  !firstChildAsString.includes("\n")
                ) {
                  return (
                    <code className={className} {...props}>
                      {childArray}
                    </code>
                  )
                }

                const language = match ? match[1].toLowerCase() : '';
                
                return (
                    <Box mb={4} mt={4} borderRadius="md" overflow="hidden">
                      <HStack justify="space-between" bg="rgb(63 63 70)" px={3} py={1.5}>
                        <Text fontSize="sm">{language}</Text>
                        <HStack spacing={1}>
                          <IconButton
                            aria-label="Download code"
                            icon={<FaDownload size={12} />}
                            size="xs"
                            variant="ghost"
                            _hover={{ color: '#1f2937', bg: 'blackAlpha.100' }}
                            onClick={() => downloadAsFile(children, language)}
                          />
                          <IconButton
                            aria-label="Copy code"
                            icon={copiedCode === String(children) ? <FaCheck size={12} /> : <FaCopy size={12} />}
                            size="xs"
                            variant="ghost"
                            _hover={{ color: '#1f2937', bg: 'blackAlpha.100' }}
                            onClick={() => onCopy(String(children))}
                          />
                        </HStack>
                      </HStack>
                      <SyntaxHighlighter
                        language={language}
                        style={oneDark}
                        customStyle={{
                          margin: 0,
                          padding: '10px',
                          backgroundColor: 'black',
                          fontSize: '15px',
                        }}
                        codeTagProps={{
                          style: {
                            backgroundColor: 'transparent',
                          },
                        }}
                        wrapLines={true}
                        lineProps={{
                          style: { backgroundColor: 'transparent' },
                        }}
                        showLineNumbers={false}
                        {...props}
                      >
                        {String(children).replace(/\n$/, '')}
                      </SyntaxHighlighter>
                    </Box>
                  );
              },
          p({ children, ...props }) {
            return (
              <Text mb={2} {...props}>
                {children}
              </Text>
            );
          },
          h1({ children, ...props }) {
            return (
              <Heading as="h1" size="xl" mt={6} mb={4} {...props}>
                {children}
              </Heading>
            );
          },
          h2({ children, ...props }) {
            return (
              <Heading as="h2" size="lg" mt={6} mb={4} {...props}>
                {children}
              </Heading>
            );
          },
          h3({ children, ...props }) {
            return (
              <Heading as="h3" size="md" mt={6} mb={4} {...props}>
                {children}
              </Heading>
            );
          },
          ul({ children, ordered, ...props }) {
            return (
              <UnorderedList mb={4} ml={6} {...props}>
                {children}
              </UnorderedList>
            );
          },
          ol({ children, ...props }) {
            return (
              <OrderedList mb={4} ml={6} {...props}>
                {children}
              </OrderedList>
            );
          },
          li({ children, ...props }) {
            return (
              <ListItem mb={2} mt={2} {...props}>
                {children}
              </ListItem>
            );
          },
          blockquote({ children, ...props }) {
            return (
              <Box
                pl={4}
                borderLeftWidth={4}
                borderLeftColor="gray.200"
                opacity={0.8}
                {...props}
              >
                <Text fontStyle="italic">{children}</Text>
              </Box>
            );
          },
          img({ alt, src, ...props }) {
            return (
              <Image
                src={src}
                alt={alt}
                maxW="full"
                borderRadius="md"
                mt={4}
                {...props}
              />
            );
          },
          table({ children, ...props }) {
            return (
              <Box overflowX="auto" my={4}>
                <Table variant="simple" width="100%" {...props}>
                  {children}
                </Table>
              </Box>
            );
          },
          thead({ children, ...props }) {
            return <Thead {...props}>{children}</Thead>;
          },
          tbody({ children, ...props }) {
            return <Tbody {...props}>{children}</Tbody>;
          },
          tr({ children, ...props }) {
            return (
              <Tr borderBottom="1px solid" borderColor="#e2e8f0" {...props}>
                {children}
              </Tr>
            );
          },
          th({ children, ...props }) {
            return (
              <Th px={4} py={2} color="#1f2937" fontWeight="bold" textAlign="left" {...props}>
                {children}
              </Th>
            );
          },
          td({ children, ...props }) {
            return (
                <Td px={4} py={2} color="#1f2937" {...props}>
                {children}
              </Td>
            );
          },
          a({ href, children, ...props }) {
                // Check if it's a download link (contains /download/)
                const isDownloadLink = href && href.includes('/download/');

                // Fix download URL to use backend API URL
                let finalHref = href;
                if (isDownloadLink) {
                  const backendBaseUrl = process.env.REACT_APP_API_URL;

                  // If the URL is relative or uses frontend URL, convert to backend URL
                  if (href.startsWith('/download/')) {
                    finalHref = `${backendBaseUrl}${href}`;
                  }
                }

                const isLoading = downloadingLink === finalHref;

                return (
                  <Box
                    as="a"
                    href={finalHref}
                    target={isDownloadLink ? undefined : "_blank"}
                    rel={isDownloadLink ? undefined : "noopener noreferrer"}
                    color={'#3498db'}
                    textDecoration="underline"
                    fontWeight="medium"
                    cursor={!href || isLoading ? "default" : "pointer"}
                    opacity={isLoading ? 0.6 : 1}
                    _hover={{
                      color: href && !isLoading ? '#2e86c1' : '#3498db',
                    }}
                    onClick={async (e) => {
                      if (!href || isLoading) {
                        e.preventDefault();
                        window.open(finalHref, '_blank');
                        return;
                      }
                      
                      if (isDownloadLink) {
                        // For download links, trigger download
                        e.preventDefault();
                        setDownloadingLink(finalHref);
                        
                        // Simple check: if response is JSON (error), show toast. Otherwise download.
                        try {
                          const response = await fetch(finalHref, {
                            credentials: 'include'
                          });
                          const contentType = response.headers.get('content-type');
                          
                          if (contentType && contentType.includes('application/json')) {
                            // It's an error response
                            const errorData = await response.json();
                            toast({
                              description: 'Code interpreter session expired',
                              status: 'error',
                              duration: 3000,
                              isClosable: true,
                            });
                          } else {
                            // It's a file, proceed with download
                            const link = document.createElement('a');
                            link.href = finalHref;
                            link.download = children;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }
                        } catch (error) {
                          toast({
                            title: 'Download failed',
                            description: 'Network error occurred',
                            status: 'error',
                            duration: 3000,
                            isClosable: true,
                          });
                        } finally {
                          setDownloadingLink(null);
                        }
                      } else {
                        // For regular links, open in new tab
                        e.preventDefault();
                        window.open(finalHref, "_blank", "noopener,noreferrer");
                      }
                    }}
                    {...props}
                  >
                    <HStack spacing={1} display="inline-flex" alignItems="center">
                      {isLoading && <Spinner size="xs" />}
                      <Text as="span">{children}</Text>
                    </HStack>
                  </Box>
                );
              },
            }}
          >
        {cleanUrlCorrectionMarkers(content)}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;
